import {FC, lazy, Suspense} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {MenuTestPage} from '../pages/MenuTestPage'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {DisableSidebar} from '../../_metronic/layout/core'
import {WithChildren} from '../../_metronic/helpers'
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import {RoleGuard} from '../../services/RoleGuard'
import {useAuth} from '../modules/auth'

const PrivateRoutes = () => {
  const {currentUser} = useAuth()
  const role = currentUser?.profile?.rol

  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const TowelFormPage = lazy(() => import('../modules/towel-form/TowelFormPage'))
  const ReturnTowelPage = lazy(() => import('../modules/return-towel/ReturnTowelPage'))
  const TowelsManagementPage = lazy(
    () => import('../modules/management/towels/TowelsManagementPage')
  )
  // const AssociatesManagementPage = lazy(
  //   () => import('../modules/management/associates/AssociatesManagementPage')
  // )
  const UsersManagementPage = lazy(() => import('../modules/management/users/UsersManagementPage'))
  const ZoneManagementPage = lazy(() => import('../modules/management/zones/ZonesManagementPage'))
  const ReportsPage = lazy(() => import('../modules/administration/reports/ReportsPage'))
  const StockPage = lazy(() => import('../modules/administration/stock/StockPage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route
          path='auth/*'
          element={
            <Navigate to={role === 'admin' ? '/admin/stock' : '/operations/towels/delivery'} />
          }
        />{' '}
        {/* Pages */}
        <Route
          path='dashboard'
          element={
            <RoleGuard allowedRoles={['admin', 'superadmin']}>
              <DashboardWrapper />
            </RoleGuard>
          }
        />{' '}
        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='operations/towels/delivery'
          element={
            <RoleGuard allowedRoles={['admin', 'operador', 'superadmin']}>
              <SuspensedView>
                <TowelFormPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        <Route
          path='operations/towels/return'
          element={
            <RoleGuard allowedRoles={['admin', 'operador', 'superadmin']}>
              <SuspensedView>
                <ReturnTowelPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        <Route
          path='management/towels'
          element={
            <RoleGuard allowedRoles={['admin', 'superadmin']}>
              <SuspensedView>
                <TowelsManagementPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        {/* <Route
          path='management/associates'
          element={
            <SuspensedView>
              <AssociatesManagementPage />
            </SuspensedView>
          }
        /> */}
        <Route
          path='management/users'
          element={
            <RoleGuard allowedRoles={['admin', 'superadmin']}>
              <SuspensedView>
                <UsersManagementPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        <Route
          path='management/zones'
          element={
            <RoleGuard allowedRoles={['admin', 'superadmin']}>
              <SuspensedView>
                <ZoneManagementPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        <Route
          path='admin/reports'
          element={
            <RoleGuard allowedRoles={['admin', 'superadmin']}>
              <SuspensedView>
                <ReportsPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        <Route
          path='admin/stock'
          element={
            <RoleGuard allowedRoles={['admin', 'superadmin']}>
              <SuspensedView>
                <StockPage />
              </SuspensedView>
            </RoleGuard>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return (
    <Suspense fallback={<TopBarProgress />}>
      <DisableSidebar>{children}</DisableSidebar>
    </Suspense>
  )
}

export {PrivateRoutes}
